import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2"; // ✅ use sweetalert2 instead of old swal
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FaTelegram } from "react-icons/fa";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";


const TemplateResearchManage = () => {
  const [tempResearch, setTempResearch] = useState([]);
  const [links, setLinks] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");

  // 📑 Pagination
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  useEffect(() => {
    fetchTemplateData(page);
  }, [page]);

  const fetchTemplateData = async (pageNumber) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}settings/research/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setTempResearch(response.data.data || []);
      setLinks(response.data.links);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔎 Apply filters
  const filteredTemplates = useMemo(() => {
    return tempResearch.filter((template) => {
      if (
        templateNameFilter &&
        !(template.templateName || "")
          .toLowerCase()
          .includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        subjectFilter &&
        !(template.message || "")
          .toLowerCase()
          .includes(subjectFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [tempResearch, templateNameFilter, subjectFilter]);

  useEffect(() => {
    if (!tempResearch.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateNameFilter, subjectFilter, tempResearch]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit));
    setPage(1);
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice(start - 1, end);

  // ✅ Delete Confirmation with SweetAlert2
  // const deleteTemplate = async (templateId) => {
  //   const result = await Swal.fire({
  //     title: "Are you sure?",
  //     text: "You will not be able to recover this record!",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#d33",
  //     cancelButtonColor: "#3085d6",
  //     confirmButtonText: "Yes, delete it!",
  //   });

  //   if (result.isConfirmed) {
  //     try {
  //       // 🔥 Call delete API here
  //       console.log(`Deleting template with ID: ${templateId}`);
  //       Swal.fire("Deleted!", "The template has been deleted.", "success");
  //     } catch (error) {
  //       Swal.fire(
  //         "Error",
  //         "Something went wrong while deleting the template.",
  //         "error"
  //       );
  //     }
  //   }
  // };

  const handleTestSms = async (templateId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/research/template/test-sms`,
        {
          params: { id: templateId },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      showToast.success("Test SMS sent successfully!");
      console.log("Test SMS Response:", response.data);
    } catch (error) {
      console.error("Error sending test SMS:", error);
      showToast.error("Failed to send Test SMS");
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async (templateId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/research/template/test`,
        {
          params: { id: templateId },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      showToast.success("Test Notification sent successfully!");
      console.log("Test Notification Response:", response.data);
    } catch (error) {
      console.error("Error sending test Notification:", error);
      showToast.error("Failed to send Test Notification");
    } finally {
      setLoading(false);
    }
  };

  const handleTestWhatsapp = async (templateId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/research/template/test-whatsapp`,
        {
          params: { id: templateId },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      showToast.success("Test WhatsApp sent successfully!");
      console.log("Test WhatsApp Response:", response.data);
    } catch (error) {
      console.error("Error sending test WhatsApp:", error);
      showToast.error("Failed to send Test WhatsApp");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setSubjectFilter("");
    };
    window.addEventListener('clearTemplateResearchFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplateResearchFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Template Research</h4>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {tempResearch.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div className="panel-heading border-0">
                <h4 className="panel-title m-0">Research Templates</h4>
              </div>
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          {loading || tempResearch.length > 0 ? (
            <div className="table-responsive">
              <table className="table m-b-0">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center", width: "50px" }}>#</th>
                    <th>Template Name</th>
                    <th>Subject</th>
                    <th style={{ width: "150px", textAlign: "center" }}>
                      Test
                    </th>
                    <th style={{ width: "80px" }}>Action</th>
                  </tr>
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search template..."
                        value={templateNameFilter}
                        onChange={(e) => setTemplateNameFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search subject..."
                        value={subjectFilter}
                        onChange={(e) => setSubjectFilter(e.target.value)}
                      />
                    </th>
                    <th></th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setTemplateNameFilter("");
                          setSubjectFilter("");
                        }}
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || filterLoading ? (
                    Array.from({ length: 10 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="skeleton-row">
                        {Array.from({ length: 5 }).map((_, colIndex) => (
                          <td key={colIndex}>
                            <div className="skeleton-box"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : paginatedTemplates.length > 0 ? (
                    paginatedTemplates.map((post, index) => (
                      <tr key={post.id}>
                        <td style={{ textAlign: "center" }}>
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                          {post.template_name}
                        </td>
                        <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                          {post.message}
                        </td>
                        <td className="text-center">
                          <button
                            onClick={() => handleTestSms(post.id)}
                            className="btn btn-default btn-xs me-1"
                            title="Test SMS"
                          >
                            <FontAwesomeIcon icon={faComment} />
                          </button>
                          <button
                            onClick={() => handleTestNotification(post.id)}
                            className="btn btn-primary btn-xs me-1"
                            title="Test Notification"
                          >
                            <FaTelegram />
                          </button>
                          <button
                            onClick={() => handleTestWhatsapp(post.id)}
                            className="btn btn-success btn-xs"
                            title="Test Whatsapp"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </button>
                        </td>
                        <td>
                          <PermissionWrapper module="settings_researchsms" action="edit">
                            <Link
                              to={`/Settings/TemplateResearch/edit/${post.id}`}
                              className="btn btn-warning btn-xs me-1"
                              title="Edit Template Research"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-3">No records found</div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default TemplateResearchManage;
